import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useGetStarted } from "@/components/GetStartedDialog";
import { Award } from "lucide-react";
import PageHead from "@/components/PageHead";

export default function Portfolio() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { open } = useGetStarted();

  useEffect(() => {
    let mounted = true;
    api
      .get("/portfolio")
      .then((r) => mounted && setItems(r.data))
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  return (
    <div data-testid="portfolio-page">
      <PageHead
        title="Web Design Portfolio | Southern California Case Studies — Redkey Web Design"
        description="Case studies from Redkey Web Design: the City of Riverside, the ADDY Award-winning RiversideTV.com, Festival of Lights, healthcare, home services, and creative brands."
        canonical="https://redkeyweb.design/work"
      />
      {/* Header */}
      <section className="mx-auto max-w-[1360px] px-6 md:px-10 pt-16 md:pt-24 pb-10">
        <p className="eyebrow">§ Archive</p>
        <h1
          data-testid="portfolio-title"
          className="font-display text-5xl md:text-7xl mt-6"
        >
          Selected <em className="not-italic text-brand-red">work.</em>
        </h1>
        <p className="mt-6 max-w-prose text-lg text-ink-soft">
          Seven long-form case studies across government, public safety,
          healthcare, home services, luxury photography and tourism. Every
          project measured on the outcomes it delivered for the client and
          their audience.
        </p>
      </section>

      <section className="mx-auto max-w-[1360px] px-6 md:px-10 pb-24 md:pb-32">
        {loading ? (
          <div className="py-32 text-center eyebrow" data-testid="portfolio-loading">
            Loading…
          </div>
        ) : (
          <ul className="border-t hairline">
            {items.map((it, idx) => (
              <li key={it.id} className="border-b hairline">
                <Link
                  to={`/work/${it.id}`}
                  data-testid={`portfolio-card-${it.id}`}
                  className="group grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 py-10 md:py-14 items-center"
                >
                  <div className="md:col-span-1 font-ui text-sm text-muted-ink tracking-widest">
                    {it.number}
                  </div>
                  <div className="md:col-span-4 order-2 md:order-none">
                    <p className="eyebrow">{it.category}</p>
                    <h2 className="font-display text-3xl md:text-5xl mt-2 group-hover:text-electric transition-colors">
                      {it.title}
                    </h2>
                    <p className="mt-2 font-ui text-sm text-muted-ink">{it.domain}</p>
                    {it.award && (
                      <span className="mt-3 inline-flex items-center gap-1.5 border border-brand-red text-brand-red px-2.5 py-1 font-ui text-[10px] tracking-[0.16em] uppercase">
                        <Award size={11} strokeWidth={2} />
                        {it.award}
                      </span>
                    )}
                  </div>
                  <div className="md:col-span-7">
                    <div className="aspect-[16/10] overflow-hidden bg-paper-alt border hairline">
                      <img
                        src={it.image}
                        alt={`${it.title} — case study cover`}
                        width="1600"
                        height="1000"
                        loading={idx === 0 ? "eager" : "lazy"}
                        decoding="async"
                        fetchPriority={idx === 0 ? "high" : "auto"}
                        className="portfolio-img w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}

        {/* CTA */}
        <div className="mt-16 md:mt-24 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <p className="font-display text-3xl md:text-4xl max-w-[24ch]">
            Ready to make yours the next one?
          </p>
          <button
            type="button"
            onClick={open}
            data-testid="portfolio-cta"
            className="inline-flex items-center gap-3 bg-ink text-paper px-6 py-4 hover:bg-electric transition-colors duration-200 font-ui"
          >
            Get Started <span aria-hidden>→</span>
          </button>
        </div>
      </section>
    </div>
  );
}
